import Box from '@mui/material/Box'
import { PickOrderDetailFragment } from '../../../../graphql'
import { PICK_ORDER_PREFIX } from '../constants'
import { PickItem } from '../picking'
import { userDisplayName } from '../userDisplayName'

export type PickListDocumentProps = {
	pickOrder: PickOrderDetailFragment,
	/** In bin order — the order the aisle is walked. */
	items: PickItem[],
}

/**
 * The run on paper: every product in bin order, with how many to take and a
 * blank to write down what was found.
 */
export default function PickListDocument( { pickOrder, items }: PickListDocumentProps ) {
	const units = items.reduce( ( sum, item ) => sum + item.toPick, 0 )

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: '8px' }}>
				<Box component="h1" sx={{ fontSize: '18pt', m: 0 }}>Pick list · {PICK_ORDER_PREFIX}{pickOrder.pickOrderNumber}</Box>
				<Box>Printed {new Date().toLocaleString()}</Box>
			</Box>
			<Box sx={{ mb: '12px', lineHeight: 1.5 }}>
				<div><strong>From:</strong> {pickOrder.fulfillingInventory.name}</div>
				<div><strong>To:</strong> {pickOrder.destinationDescription ?? '—'}</div>
				<div><strong>Picker:</strong> {userDisplayName( pickOrder.assignedToUser ) ?? 'Unassigned'}</div>
				<div><strong>Orders:</strong> {pickOrder.fulfillmentOrders.map( ( order ) => `FO-${order.orderNumber}` ).join( ', ' )}</div>
				{pickOrder.demandRequests.length > 0 && (
					<div><strong>Requests:</strong> {pickOrder.demandRequests.map( ( request ) => `REQ-${request.requestNumber}` ).join( ', ' )}</div>
				)}
				<div><strong>Total:</strong> {items.length} {items.length === 1 ? 'product' : 'products'}, {units} {units === 1 ? 'unit' : 'units'}</div>
			</Box>
			<table>
				<thead>
					<tr>
						<th>Bin</th>
						<th>Part number</th>
						<th>Manufacturer</th>
						<th>Description</th>
						<th>Orders</th>
						<th className="num">Qty</th>
						<th style={{ width: '18mm' }}>Picked</th>
					</tr>
				</thead>
				<tbody>
					{items.map( ( item ) => (
						<tr key={item.productId} style={{ breakInside: 'avoid' }}>
							<td className="mono"><strong>{item.binLocation ?? '—'}</strong></td>
							<td className="mono">{item.identity.sku ?? '—'}</td>
							<td>{item.identity.madeBy ?? '—'}</td>
							<td>{item.identity.description}</td>
							<td>
								{item.lines.map( ( claim ) => (
									<div key={claim.line.id}>FO-{claim.line.fulfillmentOrder.orderNumber} ×{claim.toPick}</div>
								) )}
							</td>
							<td className="num"><strong>{item.toPick}</strong></td>
							<td />
						</tr>
					) )}
				</tbody>
			</table>
		</Box>
	)
}
